import MULTICALL3_ABI from 'abis/multicall3.json'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { MULTICALL_3_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useMemo } from 'react'
import { Abi, Address } from 'viem'

import { useReadContractMultiple } from './useReadContractMultiple'

/**
 * Fetches the current block number and timestamp via a single batched Multicall3 read.
 * Returns undefined for data while loading, on error, or if data is not ready.
 */
export function useLatestBlock(): {
  blockNumber: bigint | undefined
  blockTimestamp: number | undefined
} {
  const { chainId } = useConnection()
  const multicallAddress = MULTICALL_3_ADDRESSES[chainId] as Address

  const enabled = !!multicallAddress
  const contracts = useMemo(() => {
    if (!enabled) return []
    return [
      {
        address: multicallAddress,
        abi: MULTICALL3_ABI as Abi,
        functionName: 'getBlockNumber',
      },
      {
        address: multicallAddress,
        abi: MULTICALL3_ABI as Abi,
        functionName: 'getCurrentBlockTimestamp',
      },
    ]
  }, [multicallAddress, enabled])

  const { data, isLoading, isError } = useReadContractMultiple({
    contracts,
    allowFailure: false,
    query: {
      ...REFETCH_POLICY_FAST, // note: it's slower than real time and no bg polling
      enabled: contracts.length > 0,
    },
  })

  return useMemo(() => {
    if (isLoading || isError || !data || data.length < 2) {
      return {
        blockNumber: undefined,
        blockTimestamp: undefined,
      }
    }

    const blockNumber = data[0].result as bigint | undefined
    const blockTimestampBigInt = data[1].result as bigint | undefined

    if (blockNumber === undefined || blockTimestampBigInt === undefined) {
      return {
        blockNumber: undefined,
        blockTimestamp: undefined,
      }
    }

    return {
      blockNumber,
      blockTimestamp: Number(blockTimestampBigInt),
    }
  }, [data, isLoading, isError])
}
